import { Box, Checkbox, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DotLegalDialog, DotLegalEmptyState, DotLegalHeader, DotLegalMultiSelect, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalTableWithControlsLocalized from "@common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized";
import EmptyStateIcon from "@common/svgs/emptyState.svg?react";
import { SelectableScenarioRow, useAddRiskScenarioDialog } from "./AddRiskScenarioDialog.hooks";
import { RiskAssessmentSubject } from "../RiskAssessmentsOverview.types";

export interface IAddRiskScenarioDialog {
    riskAreaId: string;
    subject: RiskAssessmentSubject;
    onCloseDialog: () => void;
    onScenariosAdded: () => void;
}

function AddRiskScenarioDialog(props: IAddRiskScenarioDialog) {
    const { translateString } = useTranslation();
    const {
        isLoading,
        rows,
        hasScenarios,
        categoryOptions,
        selectedCategories,
        setSelectedCategories,
        toggleScenario,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        onSaveClick,
        isSaving,
        hasSelection,
        setSearchString,
    } = useAddRiskScenarioDialog(props);

    const headers: Array<ITableHeader<SelectableScenarioRow>> = [
        { property: "name", text: translateString("scenario"), align: "left", showOnMobile: true },
        { property: "categoryLabel", text: translateString("category"), align: "left", showOnMobile: true },
    ];

    return (
        <DotLegalDialog
            open
            header={translateString("addScenario")}
            buttonOkText={translateString("add")}
            secondaryButtonText={translateString("cancel")}
            onOkClick={onSaveClick}
            onSecondaryButtonClick={props.onCloseDialog}
            onDialogClose={props.onCloseDialog}
            okButtonDisabled={!hasSelection}
            inProgress={isSaving}
            size="lg"
        >
            {!isLoading && !hasScenarios ? (
                <DotLegalEmptyState icon={<EmptyStateIcon />} text={translateString("noScenariosToAdd")} />
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", height: 700 }}>
                    <DotLegalTableWithControlsLocalized
                        headers={headers}
                        data={rows}
                        emptyText={translateString("noRiskScenariosMatchesQuery")}
                        isLoading={isLoading}
                        defaultOrderBy="name"
                        defaultOrder="asc"
                        columnsExcludedFromFilter={["id", "category", "isSelected"]}
                        getUserSpecificPageLength={() => 30}
                        clickableRows={false}
                        onControlChange={(search) => setSearchString(search)}
                        onSelectAllChanged={onSelectAllChanged}
                        headerCheckBoxState={getHeaderCheckBoxState()}
                        extraControls={
                            <Grid size={{ xs: 6 }}>
                                <DotLegalMultiSelect
                                    limit={1}
                                    onChange={setSelectedCategories}
                                    chosenOptions={selectedCategories}
                                    label={translateString("category")}
                                    placeholder={translateString("category")}
                                    options={categoryOptions}
                                    noMargin
                                    noOptionsLabel={translateString("noOptions")}
                                />
                            </Grid>
                        }
                        renderRow={(row) => (
                            <TableRow key={row.id} hover sx={{ cursor: "pointer" }} onClick={() => toggleScenario(row.id)}>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={row.isSelected} readOnly tabIndex={-1} />
                                </TableCell>
                                <TableCell>
                                    <DotLegalHeader marginBottom={0} headerStyle="extraSmall" fontWeight={400} toolTip={row.description}>
                                        {row.name}
                                    </DotLegalHeader>
                                </TableCell>
                                <TableCell>{row.categoryLabel}</TableCell>
                            </TableRow>
                        )}
                    />
                </Box>
            )}
        </DotLegalDialog>
    );
}

export default AddRiskScenarioDialog;
